/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { THomeSetting } from "apis/visitors/device";
import { theme } from "styles/theme";
import IconV2 from "uis/Icon/Icon_v2/icon";

const BtnLabel = styled.p`
    color: ${theme.neutral900};
    font-size: 20px;
    font-weight: 700;
    white-space: pre-line;
`;

const BtnAnnotation = styled.p`
    color: ${theme.neutral500};
    font-size: 12px;
    font-weight: 500;
`;

export default function CheckIn({ lang, settingInfo }: { lang: "kor" | "eng"; settingInfo: THomeSetting }) {
    return (
        <>
            <div
                css={css`
                    display: flex;
                    gap: 20px;
                    align-items: center;
                    background-color: #fff6f6;
                    padding: 20px 35px;
                    border-radius: 16px;
                    min-height: 45px;
                `}
            >
                <QRIcon />
                <div
                    css={css`
                        display: flex;
                        flex-direction: column;
                        gap: 4px;
                        flex: 1;
                    `}
                >
                    <BtnLabel>{lang === "kor" ? "예약 방문 (QR / 코드)" : `Scheduled Visit\n(QR / Code)`}</BtnLabel>
                    {lang === "kor" && (
                        <BtnAnnotation>
                            알림톡/메일로 받은
                            <br />
                            방문 상세 안내를 확인해주세요.
                        </BtnAnnotation>
                    )}
                </div>
                <IconV2 name="arrowRight" color={theme.neutral500} />
            </div>
            {settingInfo.isWalkInVisitEnabled && (
                <div
                    css={css`
                        display: flex;
                        gap: 20px;
                        align-items: center;
                        background-color: ${theme.neutral50};
                        padding: 20px 35px;
                        border-radius: 16px;
                        min-height: 45px;
                    `}
                >
                    <UserCardIcon />
                    <div
                        css={css`
                            display: flex;
                            flex-direction: column;
                            gap: 4px;
                            flex: 1;
                        `}
                    >
                        <BtnLabel>{lang === "kor" ? "당일 방문 요청" : "Register Visit"}</BtnLabel>
                        {lang === "kor" && (
                            <BtnAnnotation>
                                사전 안내를 못받으신 경우
                                <br />
                                이곳에서 방문 요청해주세요.
                            </BtnAnnotation>
                        )}
                    </div>
                    <IconV2 name={"arrowRight"} color={theme.neutral500} />
                </div>
            )}
        </>
    );
}

const QRIcon = () => {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="35" height="40" viewBox="0 0 80 92" fill="none">
            <path
                d="M0 14.2857C0 9.55357 3.83929 5.71428 8.57143 5.71428H25.7143C30.4464 5.71428 34.2857 9.55357 34.2857 14.2857V31.4286C34.2857 36.1607 30.4464 40 25.7143 40H8.57143C3.83929 40 0 36.1607 0 31.4286V14.2857ZM11.4286 17.1429V28.5714H22.8571V17.1429H11.4286ZM0 60C0 55.2679 3.83929 51.4286 8.57143 51.4286H25.7143C30.4464 51.4286 34.2857 55.2679 34.2857 60V77.1429C34.2857 81.875 30.4464 85.7143 25.7143 85.7143H8.57143C3.83929 85.7143 0 81.875 0 77.1429V60ZM11.4286 62.8571V74.2857H22.8571V62.8571H11.4286ZM54.2857 5.71428H71.4286C76.1607 5.71428 80 9.55357 80 14.2857V31.4286C80 36.1607 76.1607 40 71.4286 40H54.2857C49.5536 40 45.7143 36.1607 45.7143 31.4286V14.2857C45.7143 9.55357 49.5536 5.71428 54.2857 5.71428ZM68.5714 17.1429H57.1429V28.5714H68.5714V17.1429ZM45.7143 54.2857C45.7143 52.7143 47 51.4286 48.5714 51.4286H60C61.5714 51.4286 62.8571 52.7143 62.8571 54.2857C62.8571 55.8571 64.1429 57.1429 65.7143 57.1429H71.4286C73 57.1429 74.2857 55.8571 74.2857 54.2857C74.2857 52.7143 75.5714 51.4286 77.1429 51.4286C78.7143 51.4286 80 52.7143 80 54.2857V71.4286C80 73 78.7143 74.2857 77.1429 74.2857H65.7143C64.1429 74.2857 62.8571 73 62.8571 71.4286C62.8571 69.8571 61.5714 68.5714 60 68.5714C58.4286 68.5714 57.1429 69.8571 57.1429 71.4286V82.8571C57.1429 84.4286 55.8571 85.7143 54.2857 85.7143H48.5714C47 85.7143 45.7143 84.4286 45.7143 82.8571V54.2857ZM65.7143 85.7143C64.9565 85.7143 64.2298 85.4133 63.694 84.8774C63.1582 84.3416 62.8571 83.6149 62.8571 82.8571C62.8571 82.0994 63.1582 81.3727 63.694 80.8368C64.2298 80.301 64.9565 80 65.7143 80C66.472 80 67.1988 80.301 67.7346 80.8368C68.2704 81.3727 68.5714 82.0994 68.5714 82.8571C68.5714 83.6149 68.2704 84.3416 67.7346 84.8774C67.1988 85.4133 66.472 85.7143 65.7143 85.7143ZM77.1429 85.7143C76.3851 85.7143 75.6584 85.4133 75.1226 84.8774C74.5867 84.3416 74.2857 83.6149 74.2857 82.8571C74.2857 82.0994 74.5867 81.3727 75.1226 80.8368C75.6584 80.301 76.3851 80 77.1429 80C77.9006 80 78.6273 80.301 79.1632 80.8368C79.699 81.3727 80 82.0994 80 82.8571C80 83.6149 79.699 84.3416 79.1632 84.8774C78.6273 85.4133 77.9006 85.7143 77.1429 85.7143Z"
                fill="#FF6060"
            />
        </svg>
    );
};

const UserCardIcon = () => {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="35" height="31" viewBox="0 0 80 73" fill="none">
            <path
                d="M8.88889 5.38892C3.98611 5.38892 0 9.37503 0 14.2778V58.7222C0 63.625 3.98611 67.6111 8.88889 67.6111H71.1111C76.0139 67.6111 80 63.625 80 58.7222V14.2778C80 9.37503 76.0139 5.38892 71.1111 5.38892H8.88889ZM20 40.9445H28.8889C35.0278 40.9445 40 45.9167 40 52.0556C40 53.2778 39 54.2778 37.7778 54.2778H11.1111C9.88889 54.2778 8.88889 53.2778 8.88889 52.0556C8.88889 45.9167 13.8611 40.9445 20 40.9445ZM15.5556 27.6111C15.5556 25.2537 16.4921 22.9927 18.1591 21.3257C19.826 19.6588 22.087 18.7222 24.4444 18.7222C26.8019 18.7222 29.0628 19.6588 30.7298 21.3257C32.3968 22.9927 33.3333 25.2537 33.3333 27.6111C33.3333 29.9686 32.3968 32.2295 30.7298 33.8965C29.0628 35.5635 26.8019 36.5 24.4444 36.5C22.087 36.5 19.826 35.5635 18.1591 33.8965C16.4921 32.2295 15.5556 29.9686 15.5556 27.6111ZM51.1111 23.1667H68.8889C70.1111 23.1667 71.1111 24.1667 71.1111 25.3889C71.1111 26.6111 70.1111 27.6111 68.8889 27.6111H51.1111C49.8889 27.6111 48.8889 26.6111 48.8889 25.3889C48.8889 24.1667 49.8889 23.1667 51.1111 23.1667ZM51.1111 32.0556H68.8889C70.1111 32.0556 71.1111 33.0556 71.1111 34.2778C71.1111 35.5 70.1111 36.5 68.8889 36.5H51.1111C49.8889 36.5 48.8889 35.5 48.8889 34.2778C48.8889 33.0556 49.8889 32.0556 51.1111 32.0556ZM51.1111 40.9445H68.8889C70.1111 40.9445 71.1111 41.9445 71.1111 43.1667C71.1111 44.3889 70.1111 45.3889 68.8889 45.3889H51.1111C49.8889 45.3889 48.8889 44.3889 48.8889 43.1667C48.8889 41.9445 49.8889 40.9445 51.1111 40.9445Z"
                fill="#6E7781"
            />
        </svg>
    );
};
